import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { cancelMPAttempt, createMPAttempt, disconnectMP, getMPAttemptStatus, importAllMPTerminals, importMPTerminal, startMPOAuthFlow, updateMPTerminalOperationMode } from "../../services/integration.service";
import type { StartMPOAuthFlowData } from "../../services/integration.service";
import { ICompany } from "../../interfaces/Company";
import { IStore } from "../../interfaces/Store";

export enum MercadoPagoTerminalOperationMode {
    PDV = "PDV",
    STANDALONE = "STANDALONE",
    UNDEFINED = "UNDEFINED"
}

export interface IMPAttemptFields {
    mp_order_id?: string;
    mp_payment_id?: string;
    mp_store_id?: string;
    mp_additional?: any; // payload/metadata crudos de MP
    channel?: "pos" | "web" | "kiosk";
    idempotency_key?: string;
}
export interface IPaymentAttempt {
    companyId: string;
    storeId: string;
    orderId: string;

    provider: string;                 // <- nuevo en root
    external_reference: string;                // único
    amount: number;
    currency: string;                          // <- movido a root
    description: string;

    cashierBoxId?: string;      // hazlo required si tu flujo lo exige
    terminalId: string

    status: string;

    // Tiempos genéricos del intento
    startedAt?: Date;
    endedAt?: Date;

    // Específico de MP (opcional si provider != 'mercadopago')
    mp?: IMPAttemptFields;

    initiatedBy?: string;
    error?: string;

    // timestamps
    createdAt?: Date;
    updatedAt?: Date;
}

export interface IStartMPOAuthFlowResponse {
    authUrl: string;
}

export interface IMercadoPagoTerminalInfo {
    mp_store_id?: string;
    mp_pos_id?: string;
    mp_terminal_operating_mode?: string;
    mp_terminal_id?: string;
    mp_external_pos_id?: string;
}

export interface IPaymentTerminal {
    _id?: string;
    companyId: ICompany | string;
    storeId: IStore | string;
    provider: 'mercadopago';
    serial?: string;
    label?: string;
    mp?: IMercadoPagoTerminalInfo;
    isActive: boolean;
    createdAt?: Date;
    updatedAt?: Date;
}

export const useStartMPOAuthFlow = (data: StartMPOAuthFlowData): UseMutationResult<IStartMPOAuthFlowResponse, Error, void> => useMutation({
    mutationFn: async () => {
        try {
            const res = await startMPOAuthFlow(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res.data.data as IStartMPOAuthFlowResponse;
        } catch (e) {
            console.log('ERROR startMPOAuthFlow', e);
            throw e;
        }
    }
});

export const useDisconnectMP = () => useMutation({
    mutationFn: async () => {
        try {
            return await disconnectMP();
        } catch (e) {
            console.log('ERROR disconnectMP', e);
            throw e;
        }
    }
});

export const useImportMPTerminal = (): UseMutationResult<IPaymentTerminal[], Error, {
    storeId: string;
    mp_pos_id: string;
    label?: string;
    mp_store_id?: string;
}> => useMutation({
    mutationFn: async (data: {
        storeId: string;
        mp_pos_id: string;
        label?: string;
        mp_store_id?: string;
    }) => {
        try {
            const res = await importMPTerminal(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data?.data as IPaymentTerminal[];
        } catch (e) {
            console.log('ERROR importMPTerminal', e);
            throw e;
        }
    }
});

export const useImportAllMPTerminals = (): UseMutationResult<IPaymentTerminal[], Error, {
    storeId: string;
}> => useMutation({
    mutationFn: async (data: {
        storeId: string;
    }) => {
        try {
            const res = await importAllMPTerminals(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data?.data as IPaymentTerminal[];
        } catch (e) {
            console.log('ERROR importAllMPTerminals', e);
            throw e;
        }
    }
});

export const useCreateMPAttempt = (): UseMutationResult<IPaymentAttempt, Error, {
    orderId: string;
    amount: number;
    currency: string;
    storeId: string;
    terminalId: string;
    cashierBoxId: string;
    print_on_terminal?: 'no_ticket' | 'seller_ticket';
}> => useMutation({
    mutationFn: async (data: {
        orderId: string;
        amount: number;
        currency: string;
        storeId: string;
        terminalId: string;
        cashierBoxId: string;
        print_on_terminal?: 'no_ticket' | 'seller_ticket';
    }) => {
        try {
            const res = await createMPAttempt(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data?.data as IPaymentAttempt;
        } catch (e) {
            console.log('ERROR createMPAttempt', e);
            throw e;
        }
    }
});

export const useCancelMPAttempt = (): UseMutationResult<{ attemptId: string, status: 'cancelled', mp_order_id: string | null, mp_payment_id: string | null }, Error, {
    attemptId: string;
}> => useMutation({
    mutationFn: async (data: {
        attemptId: string;
    }) => {
        try {
            const res = await cancelMPAttempt(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data?.data as { attemptId: string, status: 'cancelled', mp_order_id: string | null, mp_payment_id: string | null };
        } catch (e) {
            console.log('ERROR cancelMPAttempt', e);
            throw e;
        }
    }
});
export const useGetMPAttemptStatus = (): UseMutationResult<{
    attemptId: string;
    status: string;
    mp_order_id: string | null;
    mp_payment_id: string | null;
}, Error, {
    attemptId: string;
}> => useMutation({
    mutationFn: async (data: {
        attemptId: string;
    }) => {
        try {
            const res = await getMPAttemptStatus(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data?.data as { attemptId: string, status: string, mp_order_id: string | null, mp_payment_id: string | null };
        } catch (e) {
            console.log('ERROR getMPAttemptStatus', e);
            throw e;
        }
    }
});

export const useUpdateMPTerminalOperationMode = (): UseMutationResult<{
    id: string;
    operating_mode: MercadoPagoTerminalOperationMode;
}, Error, {
    storeId: string;
    mp_terminal_id: string;
    operatingMode: MercadoPagoTerminalOperationMode;
}> => useMutation({
    mutationFn: async (data: {
        storeId: string;
        mp_terminal_id: string;
        operatingMode: MercadoPagoTerminalOperationMode;
    }) => {
        try {
            const res = await updateMPTerminalOperationMode(data);
            if (!res?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data as { id: string, operating_mode: MercadoPagoTerminalOperationMode };
        } catch (e) {
            console.log('ERROR updateMPTerminalOperationMode', e);
            throw e;
        }
    }
});
